import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * <DeviceViewer /> — drop-in React component for remote device viewing.
 *
 * Wraps WebRTCClient + InputHandler into a self-contained component that
 * connects on mount and disconnects on unmount.
 *
 * Exposes `sendControl` via `ref` so parent components can send
 * control messages (back, home, etc.) through the data channel.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { InputHandler } from "./input-handler.js";
import { WebRTCClient } from "./webrtc-client.js";
import { streamErrorLabel, } from "./stream-errors.js";
/**
 * How long a transient unhealthy state (ICE disconnect, DC silence,
 * brief packet loss) is allowed to persist before the UI surfaces a
 * "reconnecting" overlay. Most jitter heals inside str0m / ICE
 * restart in well under this window, so showing the overlay
 * immediately produces a constant flicker on flaky connections; the
 * user would perceive "unstable" rather than "working with recovery".
 */
const SILENT_RECOVERY_WINDOW_MS = 3_000;
export const DeviceViewer = forwardRef(function DeviceViewer({ streamParams, deviceWidth = 1080, deviceHeight = 1920, muted = true, className, style, onConnected, onDisconnected, onReconnecting, onDataChannelMessage, onVideoDimensions, onHealth, apkUploadUrl, refreshStreamParams, onStreamError, }, ref) {
    const videoRef = useRef(null);
    const clientRef = useRef(null);
    const inputRef = useRef(null);
    // Keep the latest refresh callback in a ref so `WebRTCClient`'s long
    // lifetime (it survives unrelated parent re-renders that change
    // `refreshStreamParams` identity) always invokes the freshest copy
    // without forcing a full WebRTC tear-down.
    const refreshRef = useRef(refreshStreamParams);
    useEffect(() => {
        refreshRef.current = refreshStreamParams;
    }, [refreshStreamParams]);
    const [connected, setConnected] = useState(false);
    const [reconnectAttempt, setReconnectAttempt] = useState(0);
    const [videoResolution, setVideoResolution] = useState({ width: 0, height: 0 });
    const [firstFrameReceived, setFirstFrameReceived] = useState(false);
    // `status` drives what the user sees. We intentionally stay on
    // "connected" during short recoveries so transient jitter never
    // flashes a "Reconnecting..." overlay. The `silentTimerRef` holds
    // the SILENT_RECOVERY_WINDOW_MS timer that eventually promotes a
    // persistent unhealthy state into the visible "reconnecting" UI.
    const [status, setStatus] = useState("connecting");
    const [streamFailure, setStreamFailure] = useState(null);
    const silentTimerRef = useRef(null);
    const clearSilentTimer = useCallback(() => {
        if (silentTimerRef.current !== null) {
            clearTimeout(silentTimerRef.current);
            silentTimerRef.current = null;
        }
    }, []);
    useImperativeHandle(ref, () => ({
        sendControl(msg) {
            clientRef.current?.sendControl(msg);
        },
        startCamera(facing) {
            return clientRef.current?.startCamera(facing) ?? Promise.resolve();
        },
        stopCamera() {
            return clientRef.current?.stopCamera() ?? Promise.resolve();
        },
        isCameraSharing() {
            return clientRef.current?.isCameraSharing ?? false;
        },
        retryConnect() {
            setStreamFailure(null);
            setStatus("connecting");
            return clientRef.current?.retryConnect() ?? Promise.resolve();
        },
    }), []);
    const handleConnected = useCallback(() => {
        clearSilentTimer();
        setReconnectAttempt(0);
        setStreamFailure(null);
        setConnected(true);
        setStatus("connected");
        onConnected?.();
    }, [onConnected, clearSilentTimer]);
    const handleDisconnected = useCallback((reason) => {
        clearSilentTimer();
        setConnected(false);
        // Structured codes from failTerminal use SCREAMING_SNAKE — show retry UI.
        if (/^[A-Z][A-Z0-9_]+$/.test(reason)) {
            setStreamFailure({ code: reason, message: streamErrorLabel(reason) });
            setStatus("failed");
        }
        else {
            setStatus("disconnected");
        }
        onDisconnected?.(reason);
    }, [onDisconnected, clearSilentTimer]);
    const handleStreamError = useCallback((error) => {
        clearSilentTimer();
        setConnected(false);
        setStreamFailure({ code: error.code, message: streamErrorLabel(error.code) });
        setStatus("failed");
        onStreamError?.(error);
    }, [onStreamError, clearSilentTimer]);
    const handleReconnecting = useCallback((attempt) => {
        setReconnectAttempt(attempt);
        setConnected(false);
        onReconnecting?.(attempt);
        // First attempt: give recovery a silent window before showing
        // the overlay. Subsequent attempts (i.e. we already committed
        // to a full reconnect cycle) go visible immediately because the
        // exponential-backoff delays already exceed the silent window.
        if (attempt <= 1) {
            if (silentTimerRef.current === null) {
                silentTimerRef.current = setTimeout(() => {
                    silentTimerRef.current = null;
                    setStatus("reconnecting");
                }, SILENT_RECOVERY_WINDOW_MS);
            }
        }
        else {
            clearSilentTimer();
            setStatus("reconnecting");
        }
    }, [onReconnecting, clearSilentTimer]);
    const handleHealth = useCallback((state, reason) => {
        onHealth?.(state, reason);
        if (state === "healthy") {
            // handleConnected 不是唯一的 healthy 入口 —— ICE restart
            // 静默恢复成功时，WebRTCClient 直接发 onHealth("healthy")
            // 而不会重走 onconnectionstatechange("connected")，若这里只
            // clearSilentTimer 而不推进 status，silent timer 已 fire 的
            // 场景下 overlay 会永久卡在 "reconnecting"。
            clearSilentTimer();
            setConnected(true);
            setStatus("connected");
        }
        else if (state === "recovering") {
            // Silent recovery (ICE restart, DC ping timeout, freeze
            // detection). Arm the overlay timer but keep the UI quiet.
            if (silentTimerRef.current === null) {
                silentTimerRef.current = setTimeout(() => {
                    silentTimerRef.current = null;
                    setStatus("reconnecting");
                }, SILENT_RECOVERY_WINDOW_MS);
            }
        }
        else if (state === "reconnecting") {
            // A full reconnect is in flight. We still respect the silent
            // window for the very first transition (handleReconnecting
            // will fire alongside this), but do not cancel an already-
            // armed timer. No-op here; the attempt-based branch above
            // owns the UI promotion.
        }
    }, [onHealth, clearSilentTimer]);
    useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl)
            return;
        const client = new WebRTCClient({
            streamParams,
            videoElement: videoEl,
            onConnected: handleConnected,
            onDisconnected: handleDisconnected,
            onReconnecting: handleReconnecting,
            onHealth: handleHealth,
            onStreamError: handleStreamError,
            onDataChannelMessage,
            // Indirect through the ref so consumers can hand us a freshly
            // memoised callback on every render without forcing the WebRTC
            // session to tear down. When the parent never wired one up
            // (`refreshRef.current` stays undefined for the client's whole
            // lifetime) we leave `refreshStreamParams` unset so the client's
            // own short-circuit keeps reusing the cached params instead of
            // logging a "refresh failed" warning on every reconnect.
            refreshStreamParams: refreshStreamParams
                ? () => {
                    const fn = refreshRef.current;
                    if (!fn)
                        return Promise.reject(new Error("refreshStreamParams not configured"));
                    return fn();
                }
                : undefined,
        });
        clientRef.current = client;
        const input = new InputHandler({
            client,
            videoElement: videoEl,
            deviceWidth,
            deviceHeight,
        });
        inputRef.current = input;
        input.attach();
        setStatus("connecting");
        setStreamFailure(null);
        // `WebRTCClient.connect` now swallows first-attempt errors into
        // its own exponential-backoff loop (see A3). The `.catch` is kept
        // as a belt-and-braces for impossible-in-practice sync throws.
        client.connect().catch((err) => {
            console.error("[DeviceViewer] connection failed:", err);
        });
        return () => {
            clearSilentTimer();
            input.detach();
            client.disconnect();
            clientRef.current = null;
            inputRef.current = null;
        };
        // Re-connect when streamParams identity changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [streamParams.mqttUrl, streamParams.deviceTopic, streamParams.mqttToken]);
    useEffect(() => {
        inputRef.current?.updateDeviceResolution(deviceWidth, deviceHeight);
    }, [deviceWidth, deviceHeight]);
    useEffect(() => {
        const video = videoRef.current;
        if (!video)
            return;
        const updateResolution = () => {
            const width = video.videoWidth;
            const height = video.videoHeight;
            setVideoResolution((current) => current.width === width && current.height === height
                ? current
                : { width, height });
        };
        const handlePlaying = () => setFirstFrameReceived(true);
        const handleEmptied = () => setFirstFrameReceived(false);
        video.addEventListener("loadedmetadata", updateResolution);
        video.addEventListener("resize", updateResolution);
        video.addEventListener("playing", handlePlaying);
        video.addEventListener("emptied", handleEmptied);
        updateResolution();
        return () => {
            video.removeEventListener("loadedmetadata", updateResolution);
            video.removeEventListener("resize", updateResolution);
            video.removeEventListener("playing", handlePlaying);
            video.removeEventListener("emptied", handleEmptied);
        };
    }, []);
    const handleDrop = useCallback(async (e) => {
        e.preventDefault();
        const file = e.dataTransfer?.files[0];
        if (!file || !file.name.endsWith(".apk"))
            return;
        if (apkUploadUrl) {
            const form = new FormData();
            form.append("apk", file);
            try {
                const resp = await fetch(apkUploadUrl, { method: "POST", body: form });
                if (!resp.ok)
                    console.error("[DeviceViewer] APK upload failed:", resp.status);
            }
            catch (err) {
                console.error("[DeviceViewer] APK upload error:", err);
            }
        }
    }, [apkUploadUrl]);
    const handleDragOver = useCallback((e) => e.preventDefault(), []);
    return (_jsxs("div", { className: className, "data-viewer-status": status, "data-reconnect-attempt": reconnectAttempt, "data-video-width": videoResolution.width, "data-video-height": videoResolution.height, "data-first-frame": firstFrameReceived ? "true" : "false", "data-viewer-failure": status === "failed" ? (streamFailure?.code ?? "failed") : status === "disconnected" ? "disconnected" : "none", style: {
            position: "relative",
            display: "block",
            background: "#000",
            aspectRatio: `${videoResolution.width || deviceWidth} / ${videoResolution.height || deviceHeight}`,
            ...style,
        }, onDrop: handleDrop, onDragOver: handleDragOver, children: [_jsx("video", { ref: videoRef, autoPlay: true, playsInline: true, muted: muted, "data-testid": "device-video", onLoadedMetadata: (event) => {
                    const { videoWidth, videoHeight } = event.currentTarget;
                    if (videoWidth > 0 && videoHeight > 0) {
                        onVideoDimensions?.(videoWidth, videoHeight);
                    }
                }, style: { width: "100%", height: "100%", objectFit: "contain", display: "block" } }), status === "connecting" && (_jsx("div", { style: {
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 14,
                    background: "rgba(0,0,0,0.6)",
                }, children: "Connecting..." })), status === "reconnecting" && (_jsx("div", { style: {
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ffa500",
                    fontSize: 14,
                    background: "rgba(0,0,0,0.4)",
                    // Eat pointer events so a frozen last-frame does not accept
                    // swipes that DataChannel will silently drop.
                    pointerEvents: "auto",
                }, children: "Reconnecting..." })), status === "failed" && (_jsxs("div", { "data-testid": "stream-failure", "data-stream-error-code": streamFailure?.code ?? "", style: {
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    color: "#ff6b6b",
                    fontSize: 14,
                    background: "rgba(0,0,0,0.6)",
                    pointerEvents: "auto",
                }, children: [_jsx("div", { children: streamFailure?.message ?? "Stream connection failed" }), streamFailure?.code ? (_jsx("div", { style: { color: "#aaa", fontSize: 11 }, children: streamFailure.code })) : null, _jsx("button", { type: "button", "data-testid": "stream-reconnect", onClick: () => {
                            setStreamFailure(null);
                            setStatus("connecting");
                            void clientRef.current?.retryConnect();
                        }, style: {
                            cursor: "pointer",
                            border: "1px solid #ffa500",
                            background: "transparent",
                            color: "#ffa500",
                            borderRadius: 6,
                            padding: "6px 12px",
                            fontSize: 13,
                        }, children: "Reconnect" })] })), status === "disconnected" && (_jsx("div", { style: {
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ff6b6b",
                    fontSize: 14,
                    background: "rgba(0,0,0,0.6)",
                }, children: "Disconnected" }))] }));
});
